<template>
  <div 
    class="toast-container"
    v-show="isShow"
    :class="{ fadeIn: isShow, fadeOut: !isShow }"
  >
    <!-- 优化为「标题+内容」结构 -->
    <div class="toast-content" :style="toastStyle">
      <!-- 标题：可选，有标题才显示 -->
      <div class="toast-title" v-if="title">{{ title }}</div>
      <!-- 内容：必显，兼容原有 message 逻辑 -->
      <div class="toast-message">{{ message }}</div>
      <span class="toast-close" @click="closeToast" v-if="showClose">×</span>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      title: '', // 新增标题字段
      message: '',
      isShow: false,
      showClose: false,
      duration: 2000,
      timer: null,
      type: 'default' // success/error/warning/default
    };
  },
  computed: {
    toastStyle() {
      // 响应式样式：仅适配手机、平板2套布局
      const screenWidth = document.documentElement.clientWidth;
      let padding = '16px 24px';
      let fontSize = '14px';

      if (screenWidth < 768) { // 手机端（主流手机尺寸）
        padding = '12px 20px';
        fontSize = '13px';
      } else { // 平板+大屏端（统一适配，大屏最大宽度500px）
        padding = '18px 28px';
        fontSize = '15px';
      }

      // 80%透明度背景色 + 高反差文字色（保留原有色调，仅添加透明度）
      const styleMap = {
        success: {
          backgroundColor: 'rgba(46, 125, 50, 0.8)', // 深绿色（80%透明）
          color: '#FFFFFF', // 纯白色（高反差）
          titleBgColor: 'rgba(56, 142, 60, 0.8)' // 标题背景色（80%透明）
        },
        error: {
          backgroundColor: 'rgba(211, 47, 47, 0.8)', // 深红色（80%透明）
          color: '#FFFFFF', // 纯白色（高反差）
          titleBgColor: 'rgba(229, 57, 53, 0.8)' // 标题背景色（80%透明）
        },
        warning: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)', // 黑色（80%透明）
          color: '#FFFFFF', // 纯白色（高反差）
          titleBgColor: 'rgba(0, 0, 0, 0.8)' // 标题背景色（80%透明）
        },
        info: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)', // 黑色（80%透明）
          color: '#FFFFFF', // 纯白色（高反差）
          titleBgColor: 'rgba(0, 0, 0, 0.8)' // 标题背景色（80%透明）
        },
        default: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)', // 黑色（80%透明）
          color: '#FFFFFF', // 纯白色（高反差）
          titleBgColor: 'rgba(0, 0, 0, 0.8)' // 标题背景色（80%透明）
        }
      };

      return {
        padding,
        fontSize,
        ...styleMap[this.type]
      };
    }
  },
  methods: {
    show(options = {}) {
      this.title = options.title || ''; // 接收标题参数（可选）
      this.message = options.message || '提示信息';
      this.duration = options.duration || 2000;
      this.showClose = options.showClose || false;
      this.type = options.type || 'default';
      this.isShow = true;

      if (this.timer) clearTimeout(this.timer);

      if (this.duration > 0) {
        this.timer = setTimeout(() => {
          this.closeToast();
        }, this.duration);
      }
    },
    closeToast() {
      this.isShow = false;
      setTimeout(() => {
        this.title = ''; // 清空标题
        this.message = '';
      }, 300);
      clearTimeout(this.timer);
    }
  },
  beforeDestroy() {
    clearTimeout(this.timer);
  }
};
</script>

<style scoped>
.toast-container {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 9999;
  transition: opacity 0.3s ease, transform 0.3s ease;
  opacity: 0;
  transform: translate(-50%, -50%) scale(0.9);
}

.toast-container.fadeIn {
  opacity: 1;
  transform: translate(-50%, -50%) scale(1);
}

.toast-container.fadeOut {
  opacity: 0;
  transform: translate(-50%, -50%) scale(0.9);
}

.toast-content {
  border-radius: 8px;
  max-width: 90vw; /* 手机端：占满屏幕宽度90% */
  width: fit-content; /* 内容撑满宽度（不超过最大宽度） */
  max-width: 500px; /* 大屏端：最大宽度限制500px，内容撑满再换行 */
  min-width: 500px; /* 最小宽度500px，保持布局稳定 */
  text-align: center;
  position: relative;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2); /* 增强阴影，提升视觉层次 */
  letter-spacing: 0.5px; /* 字间距微调，提升可读性 */
  overflow: hidden; /* 确保标题背景色不溢出圆角 */
}

/* 标题样式：加粗放大，独立背景色，与内容区分 */
.toast-title {
  font-size: 1.2em; /* 相对于父容器字体大小放大 */
  font-weight: 700;
  margin-bottom: 6px; /* 与内容保持间距 */
  line-height: 1.3;
  padding: 4px 0; /* 标题内边距，让背景色更饱满 */
  border-radius: 8px 8px 0 0; /* 顶部圆角与容器对齐 */
}

/* 内容样式：保持清晰可读，英文单词不截断 */
.toast-message {
  font-weight: 400;
  line-height: 1.5;
  word-break: keep-all; /* 不截断英文单词（中文也不会截断） */
  white-space: pre-wrap; /* 保留换行符，长文本自动换行 */
}

.toast-close {
  position: absolute;
  top: 8px;
  right: 12px;
  font-size: 18px;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.9); /* 关闭按钮高反差 */
  transition: color 0.2s, transform 0.2s;
  line-height: 1;
  z-index: 1; /* 确保关闭按钮在标题上方 */
}

.toast-close:hover {
  color: #FFFFFF;
  transform: scale(1.1); /* hover放大，提升交互感 */
}

/* 仅保留手机端响应式优化（平板+大屏共用基础样式） */
@media (max-width: 767px) {
  .toast-content {
    min-width: 90vw; /* 手机端最小宽度=最大宽度，充分利用屏幕 */
    padding: 12px 20px !important;
    font-size: 13px !important;
  }
  .toast-title {
    font-size: 1.1em;
    margin-bottom: 4px;
  }
}
</style>